import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ExternalLink, Apple, Play, Star, Download } from 'lucide-react';
import { apps } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const AppCard = ({ app, t }) => {
  const { language } = useLanguage();

  // Get translated app details by the app's translation key
  const appDetails = t.appDetails[app.appKey] || { tagline: app.tagline, description: app.description };
  const translatedCategory = t.categories[app.category] || app.category;

  return (
    <div 
      className="group relative bg-[#302f2c] border border-[#1e3a5f]/50 overflow-hidden transition-all duration-500 hover:border-[#7dd3fc]/50"
    >
      {/* Image Section */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={app.image}
          alt={app.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c1b] via-[#1a1c1b]/40 to-transparent" />
        
        {/* Category Badge */}
        <Badge className="absolute top-4 left-4 bg-[#1e3a5f] text-[#7dd3fc] border-none font-medium">
          {translatedCategory}
        </Badge>

        {/* Featured Badge */}
        {app.featured && (
          <Badge className="absolute top-4 right-4 bg-[#7dd3fc] text-[#1a1c1b] border-none font-bold">
            {t.apps.featured}
          </Badge>
        )}
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* App Name & Tagline */}
        <h3 className="text-[#7dd3fc] font-bold text-2xl mb-1 group-hover:text-[#7dd3fc]/90 transition-colors">
          {app.name}
        </h3>
        <p className="text-[#888680] text-sm font-medium mb-3">{appDetails.tagline}</p>
        
        {/* Description */}
        <p className="text-[#7dd3fc]/80 text-base leading-relaxed mb-4 line-clamp-3">
          {appDetails.description}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-5 text-sm">
          <div className="flex items-center gap-1 text-[#7dd3fc]">
            <Download size={14} />
            <span className="font-semibold">{app.stats.downloads}</span>
          </div>
          <div className="flex items-center gap-1 text-[#f8d47a]">
            <Star size={14} fill="currentColor" />
            <span className="font-semibold">{app.stats.rating}</span>
          </div>
          <span className="text-[#888680]">{app.stats.reviews} {t.apps.reviews}</span>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-4 py-2 font-semibold text-xs uppercase tracking-wide transition-transform hover:scale-[1.02] active:scale-[0.98] flex items-center gap-1.5"
            onClick={() => window.open(app.websiteUrl, '_blank')}
          >
            <ExternalLink size={14} />
            {t.apps.website}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[#888680] text-[#888680] hover:border-[#7dd3fc] hover:text-[#7dd3fc] rounded-full px-4 py-2 font-semibold text-xs uppercase tracking-wide transition-all flex items-center gap-1.5"
            onClick={() => window.open(app.appStoreUrl, '_blank')}
          >
            <Apple size={14} />
            iOS
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[#888680] text-[#888680] hover:border-[#7dd3fc] hover:text-[#7dd3fc] rounded-full px-4 py-2 font-semibold text-xs uppercase tracking-wide transition-all flex items-center gap-1.5"
            onClick={() => window.open(app.playStoreUrl, '_blank')}
          >
            <Play size={14} />
            Android
          </Button>
        </div>
      </div>

      {/* Hover accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#7dd3fc] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
    </div>
  );
};

const AppsShowcase = () => {
  const [filter, setFilter] = useState('all');
  const { language } = useLanguage();
  const t = translations[language];
  
  const categories = ['all', ...new Set(apps.map(app => app.category))];
  
  const filteredApps = filter === 'all' 
    ? apps 
    : apps.filter(app => app.category === filter);

  return (
    <section id="apps" className="bg-[#1a1c1b] py-24 md:py-32">
      <div className="max-w-[87.5rem] mx-auto px-10">
        {/* Section Header */}
        <div className="mb-16">
          <span className="text-[#888680] text-sm font-semibold uppercase tracking-widest mb-4 block">
            {t.apps.label}
          </span>
          <h2 className="font-black text-[#7dd3fc] uppercase leading-[0.85] mb-6" style={{ fontSize: 'clamp(3rem, 4vw + 1rem, 5.5rem)' }}>
            {t.apps.headline}
          </h2>
          <p className="text-[#7dd3fc]/70 text-xl max-w-2xl">
            {t.apps.description}
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((category) => {
            const displayName = category === 'all' 
              ? t.apps.all 
              : t.categories[category] || category;
            
            return (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-5 py-2.5 rounded-full font-semibold text-sm uppercase tracking-wide transition-all duration-300 ${
                  filter === category
                    ? 'bg-[#7dd3fc] text-[#1a1c1b]'
                    : 'bg-[#302f2c] text-[#888680] hover:text-[#7dd3fc] hover:bg-[#1e3a5f]'
                }`}
              >
                {displayName}
              </button>
            );
          })}
        </div>

        {/* Apps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredApps.map((app) => (
            <AppCard key={app.id} app={app} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppsShowcase;
